import { Form, Input, InputNumber, Select } from "antd";
import { Rule } from "antd/lib/form";
import { useGetSortCodeTypeEnumQuery } from "app/configApiSlice";

export interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: "number" | "text";
  record: Record<string, any>;
  index: number;
  children: React.ReactNode;
  showErrorField?: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  dataIndex,
  inputType,
  record,
  editing,
  children,
  showErrorField,
  ...restProps
}) => {
  const sortCodeQuery = useGetSortCodeTypeEnumQuery();

  /**
   * Error-field mapping table
   */
  const errorMap: Record<string, keyof typeof record> = {
    "Account Name": "accountNameHasError",
    "Account Number": "accountNumberHasError",
    "Sort Code": "sortCodeHasError",
    "Merchant ID": "merchantHasError",
    "Narration": "narrationHasError",
    "Payor": "payorHasError",
  };

  /**
   * Determine if error highlight is required
   */
  const resolvedShowErrorField =
    showErrorField ?? Boolean(errorMap[title] && record[errorMap[title]]);

  /**
   * Generate input component
   */
  const getInputNode = () => {
    // Special-case for Sort Code
    if (title === "Sort Code") {
      return (
        <Select
          showSearch
          placeholder="Sort Code"
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          style={{ fontSize: 13 }}
        >
          {sortCodeQuery.data?.response?.map((item) => (
            <Select.Option key={item.id} value={item.code} style={{ fontSize: 13 }}>
              {item.code}
            </Select.Option>
          ))}
        </Select>
      );
    }

    if (inputType === "number") return <InputNumber style={{ fontSize: 13 }} />;

    return <Input style={{ fontSize: 13 }} />;
  };

  /**
   * Validation rules
   */
  const getRules = () => {
    const rules: Rule[] = [{ required: true, message: `Input ${title}!` }];

    if (title === "Account Number") {
      rules.push({ min: 10, max: 10, message: "must be 10 digits" });
    }

    return rules;
  };

  /**
   * Editable visibility rules
   */
  const shouldShowEditable =
    showErrorField !== undefined ? editing && resolvedShowErrorField : editing;

  return (
    <td {...restProps}>
      {shouldShowEditable ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0, fontSize: 13 }}
          rules={getRules()}
        >
          {getInputNode()}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export default EditableCell;
